import { arenaIsOpen, controlsEnabled, holdsHunt, type PvpUiState } from './machine';

/**
 * What the shell shows, derived once.
 *
 * ## Why this is a module and not a handful of comparisons
 *
 * `state.kind === 'running'` scattered across the App, the top bar, the HUD and
 * the panel is four places that have to agree, and they only agree until
 * somebody adds a sixteenth state. Then one of them is forgotten, and what the
 * player gets is the hunt's HUD over a duel, or a PvP button that opens a
 * second lobby over a fight.
 *
 * So the presentation is one pure function of the state, every flag is derived
 * from the same value, and a test walks all fifteen states and asserts the
 * count - a new state fails that test before it can be forgotten in a fifth
 * place.
 *
 * ## Everything here answers "what is on screen", never "what is true"
 *
 * Whether the hunt is PAUSED is the coordinator's business and whether the
 * arena is OPEN is the controller's; those are already answered by `holdsHunt`
 * and `arenaIsOpen` and are only read here. Nothing in this file may decide a
 * duel, and nothing may reach for a match, a socket or a result.
 */

export interface PvpPresentation {
  /** The duel panel is on screen at all. */
  readonly panelOpen: boolean;
  /**
   * This tab is asking the server whether it is already in a duel.
   *
   * Nothing is drawn while this is true - the panel opens only when there is
   * something to open it for. It is published because the SHELL needs it: the
   * hunt's own overlays must stand aside for an arena that is one answer away,
   * or a death from a hunt that ended earlier lands on top of a match that is
   * about to be restored.
   */
  readonly restoring: boolean;
  /**
   * The dedicated mode: the duel owns the screen.
   *
   * From the moment the transition starts, so there is never a frame with both
   * modalities visible - which is the whole reason `entering_pvp` exists.
   */
  readonly dedicatedMode: boolean;
  /** The duel's own HUD - health, connection, leave - is shown. */
  readonly pvpHudVisible: boolean;
  /** The hunt's HUD is shown. The exact complement of the dedicated mode. */
  readonly huntHudVisible: boolean;
  /** The shell's tabs: hunt, maps, mining, store, inventory. */
  readonly shellNavigationVisible: boolean;
  /** The PVP button may be pressed. */
  readonly pvpButtonEnabled: boolean;
  /** A close/leave affordance is offered. */
  readonly canClose: boolean;
  /**
   * An abandonment is travelling to the room and has not been answered.
   *
   * The button is busy, says so, and must not be pressable: the confirmation is
   * the server's `ended` frame, and a second click before it arrives would be a
   * second intent for a match that is already being decided.
   */
  readonly abandonPending: boolean;
  /**
   * The intent could not be sent at all, so nothing was abandoned.
   *
   * The player stays in the match and the button offers another attempt. This is
   * deliberately not `showsError`, which replaces the screen - a fight the player
   * is still in must not be covered by a refusal about leaving it.
   */
  readonly abandonFailed: boolean;
  /** The PRONTO button is shown and pressable. */
  readonly canReady: boolean;
  /** WASD and the pointer drive a ship. Only ever while the room runs. */
  readonly controlsEnabled: boolean;
  /** The "returning to the game" screen, with its retry. */
  readonly showsRecovery: boolean;
  /** A result screen the SERVER produced. Never derived from anything else. */
  readonly showsResult: boolean;
  /** The reconnecting overlay. */
  readonly showsReconnecting: boolean;
  /** A refusal worth showing, with whatever the state says about retrying. */
  readonly showsError: boolean;
}

/**
 * Everything the shell needs, from one value.
 *
 * `dedicatedMode` is `holdsHunt` and not a list of its own: the duel owns the
 * screen exactly while it is holding the hunt down, and writing that twice is
 * how the two drift apart.
 */
export function pvpPresentation(state: PvpUiState): PvpPresentation {
  const dedicated = holdsHunt(state);
  const arena = arenaIsOpen(state);

  return {
    /*
     * `restoring` is deliberately NOT a panel.
     *
     * It holds nothing and shows nothing; treating it as an open panel would
     * put the duel screen over the hunt on every page load, which is the thing
     * `resume()` refuses to do.
     */
    panelOpen: state.kind !== 'closed' && state.kind !== 'restoring',
    restoring: state.kind === 'restoring',
    dedicatedMode: dedicated,
    /*
     * The duel's HUD follows the ARENA, not the dedicated mode.
     *
     * `entering_pvp` and `recovering_hunt` are dedicated and have no arena -
     * one is putting it up and the other has already taken it down - and a HUD
     * over an empty screen showing a health bar for a ship that is not drawn is
     * worse than no HUD.
     */
    pvpHudVisible: arena,
    huntHudVisible: !dedicated,
    shellNavigationVisible: !dedicated,
    /*
     * The button opens the panel, so it is only pressable while there is no
     * panel. It is not disabled during a duel - it is not on screen at all,
     * because the shell's navigation is gone.
     */
    pvpButtonEnabled: state.kind === 'closed',
    /*
     * A way out from everywhere except the moments that are already on their
     * way out, or mid-transaction.
     *
     * `entering_pvp` is a synchronous sequence with a rollback; `leaving` is
     * already going; and `recovering_hunt` IS the exit - it is waiting on the
     * server to re-authorise the hunt, and it offers its own retry. A "leave"
     * button in any of the three would mean restarting something halfway
     * through, and in recovery it would start a second bootstrap over the
     * first.
     */
    canClose:
      state.kind !== 'closed' &&
      state.kind !== 'entering_pvp' &&
      state.kind !== 'leaving' &&
      state.kind !== 'recovering_hunt',
    abandonPending: state.kind === 'running' && state.abandon === 'sending',
    abandonFailed: state.kind === 'running' && state.abandon === 'failed',
    // Only in `ready`, and only until this generation has declared.
    canReady: state.kind === 'ready' && !state.declared,
    controlsEnabled: controlsEnabled(state),
    showsRecovery: state.kind === 'recovering_hunt',
    showsResult: state.kind === 'completed',
    showsReconnecting: state.kind === 'reconnecting',
    showsError: state.kind === 'error',
  };
}
